import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Stack,
    Avatar,
    Chip,
    Drawer,
    IconButton,
    Pagination,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Tooltip as MuiTooltip,
    Paper,
    useTheme,
} from '@mui/material';
import { Close as CloseIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell,
    ReferenceLine,
} from 'recharts';
import { fetchDailyPoliticalMentions, fetchCandidateMentions } from '../../api/podcasts';
import { convertToCSV, downloadCSV, createDateRangeFilename } from '../../utils/csvUtils';
import { CsvDownloadButton } from '../CsvDownloadButton';
import {
    CANDIDATE_COLORS,
    CANDIDATE_IMAGES,
    TOPIC_COLORS,
    ELECTION_MILESTONES,
} from './electionColors';

// Custom label component for reference lines with tooltip
const ReferenceLineLabel = ({ viewBox, value, fill, fullLabel }: any) => {
    const theme = useTheme();
    const { x, y } = viewBox;
    // Position label ABOVE the chart area, centered on the reference line
    const labelY = y - 18;
    const rectHeight = 20;
    const rectWidth = 70;

    // Always center the label horizontally on the reference line
    const rectX = x - rectWidth / 2;
    const textX = x;

    return (
        <g>
            <title>{fullLabel}</title>
            <rect
                x={rectX}
                y={labelY - rectHeight / 2}
                width={rectWidth}
                height={rectHeight}
                rx={6}
                fill={theme.palette.background.paper}
                stroke={fill}
                strokeWidth={1.5}
                style={{ cursor: 'help', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
            />
            <text
                x={textX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={fill}
                fontSize={10}
                fontWeight={600}
                style={{ cursor: 'help' }}
            >
                {value}
            </text>
        </g>
    );
};

// Helper to find the formatted date label for a milestone in chartData
const findMilestoneLabel = (chartData: any[], targetDate: string): string | null => {
    // For weekly data, find the week that contains the target date
    const targetDateObj = new Date(targetDate + 'T12:00:00.000Z');

    for (const entry of chartData) {
        const entryDate = new Date(entry.fullDate + 'T12:00:00.000Z');

        // Check if dates match exactly (for daily view)
        if (entry.fullDate === targetDate) {
            return entry.date;
        }

        // For weekly view, check if target falls within the week (entry date to +6 days)
        const weekEnd = new Date(entryDate);
        weekEnd.setDate(weekEnd.getDate() + 6);

        if (targetDateObj >= entryDate && targetDateObj <= weekEnd) {
            return entry.date;
        }
    }
    return null;
};


interface CandidatesTabProps {
    selectedRange: {
        from: string;
        to: string;
        defaultView: 'daily' | 'weekly';
    };
    includeRadio: boolean;
    isActive: boolean;
}

interface DailyMentionData {
    date: string;
    [candidate: string]: any;
}

export function CandidatesTab({ selectedRange, includeRadio, isActive }: CandidatesTabProps) {
    const theme = useTheme();
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCandidatesForChart, setSelectedCandidatesForChart] = useState<string[]>([]);
    const [selectedTopicsForChart, setSelectedTopicsForChart] = useState<string[]>([]);
    const [weeklyStatus, setWeeklyStatus] = useState<'daily' | 'weekly'>(selectedRange.defaultView);

    // Update weeklyStatus when selectedRange changes
    React.useEffect(() => {
        setWeeklyStatus(selectedRange.defaultView);
    }, [selectedRange.defaultView]);

    // Fetch daily mention data for candidates - only when tab is active
    const { data: dailyMentions = [], isLoading, error } = useQuery<DailyMentionData[]>({
        queryKey: ['daily-political-mentions', selectedRange.from, selectedRange.to, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
                entityType: 'candidate',
                period: weeklyStatus,
            });
        },
        enabled: isActive,
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        gcTime: 24 * 60 * 60 * 1000, // 1 day
    });

    // Fetch daily mention data for topics - only when tab is active AND topics are selected
    const { data: dailyTopicMentions = [] } = useQuery<DailyMentionData[]>({
        queryKey: ['daily-political-mentions-topics', selectedRange.from, selectedRange.to, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
                entityType: 'topic',
                period: weeklyStatus,
            });
        },
        enabled: isActive && selectedTopicsForChart.length > 0,
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        gcTime: 24 * 60 * 60 * 1000, // 1 day
    });

    // Fetch candidate mentions for sidebar
    const { data: candidateMentionsData, isLoading: isLoadingMentions } = useQuery({
        queryKey: ['candidate-mentions', selectedCandidate, selectedRange.from, selectedRange.to, includeRadio, currentPage],
        queryFn: async () => {
            if (!selectedCandidate) return null;
            return fetchCandidateMentions({
                candidateName: selectedCandidate,
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
                page: currentPage,
                limit: 10,
            });
        },
        enabled: !!selectedCandidate && sidebarOpen,
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        gcTime: 24 * 60 * 60 * 1000, // 1 day
    });

    const handleCandidateClick = (candidateName: string) => {
        setSelectedCandidate(candidateName);
        setCurrentPage(1);
        setSidebarOpen(true);
    };

    const handleCloseSidebar = () => {
        setSidebarOpen(false);
        setSelectedCandidate(null);
        setCurrentPage(1);
    };

    const handleCandidateSelectionChange = (event: any) => {
        const value = event.target.value;
        setSelectedCandidatesForChart(typeof value === 'string' ? value.split(',') : value);
    };

    const createYouTubeUrlWithTimestamp = (url: string, startTime: number) => {
        try {
            const urlObj = new URL(url);
            const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop();
            const timestampSeconds = Math.floor(startTime);
            return `https://www.youtube.com/watch?v=${videoId}&t=${timestampSeconds}s`;
        } catch {
            return url; // Fallback to original URL if parsing fails
        }
    };

    // CSV Download Functions
    const downloadDailyMentionsCSV = async () => {
        if (!chartData.length) return;

        try {
            // Fetch data for both filter states
            const [podcastsOnlyData, allChannelsData] = await Promise.all([
                fetchDailyPoliticalMentions({
                    from: selectedRange.from,
                    to: selectedRange.to,
                    includeRadio: false,
                    entityType: 'candidate',
                    period: weeklyStatus,
                }),
                fetchDailyPoliticalMentions({
                    from: selectedRange.from,
                    to: selectedRange.to,
                    includeRadio: true,
                    entityType: 'candidate',
                    period: weeklyStatus,
                })
            ]);

            // Create a map for easier data lookup
            const podcastsMap = new Map();
            const allChannelsMap = new Map();

            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(key => {
                    if (key !== 'date' && key !== 'fullDate') {
                        const mapKey = `${dayData.date}_${key}`;
                        // Handle the nested structure with .count
                        const value = dayData[key]?.count || dayData[key] || 0;
                        podcastsMap.set(mapKey, value);
                    }
                });
            });

            allChannelsData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(key => {
                    if (key !== 'date' && key !== 'fullDate') {
                        const mapKey = `${dayData.date}_${key}`;
                        // Handle the nested structure with .count
                        const value = dayData[key]?.count || dayData[key] || 0;
                        allChannelsMap.set(mapKey, value);
                    }
                });
            });

            // Get all unique dates
            const allDates = Array.from(new Set([...podcastsOnlyData.map((item: any) => item.date), ...allChannelsData.map((item: any) => item.date)])).sort();

            const csvData = allDates.map(date => {
                const row: any = {
                    'Fecha': date,
                };

                // Add each selected candidate's mentions for this day (grouped side by side)
                selectedCandidatesForChart.forEach(candidateName => {
                    const podcastsKey = `${date}_${candidateName}`;
                    const podcastsOnly = podcastsMap.get(podcastsKey) || 0;
                    const allChannels = allChannelsMap.get(podcastsKey) || 0;

                    row[`${candidateName} - Total`] = allChannels;
                    row[`${candidateName} - Solo Podcasts`] = podcastsOnly;
                });

                return row;
            });

            // Create grouped header row for side-by-side presentation
            const headerRow: any = { 'Fecha': '' };
            selectedCandidatesForChart.forEach(candidateName => {
                headerRow[`${candidateName} - Total`] = candidateName;
                headerRow[`${candidateName} - Solo Podcasts`] = '';
            });

            // Create subheader row
            const subHeaderRow: any = { 'Fecha': 'Fecha' };
            selectedCandidatesForChart.forEach(candidateName => {
                subHeaderRow[`${candidateName} - Total`] = 'Total';
                subHeaderRow[`${candidateName} - Solo Podcasts`] = 'Solo Podcasts';
            });

            // Combine header rows with data
            const csvDataWithHeaders = [headerRow, subHeaderRow, ...csvData];
            const csvContent = convertToCSV(csvDataWithHeaders);
            const filename = createDateRangeFilename(`menciones_${weeklyStatus === 'daily' ? 'diarias' : 'semanales'}_candidatos_completo`, selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading daily mentions CSV:', error);
        }
    };

    const downloadTotalMentionsCSV = async () => {
        if (!candidateStats.length) return;

        try {
            // Fetch stats for podcasts only
            const podcastsOnlyData = await fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio: false,
                entityType: 'candidate',
                period: weeklyStatus,
            });

            // Calculate totals for podcasts only
            const podcastsTotals = new Map();
            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(candidateName => {
                    if (candidateName !== 'date' && candidateName !== 'fullDate') {
                        const current = podcastsTotals.get(candidateName) || 0;
                        // Handle the nested structure with .count
                        const value = dayData[candidateName]?.count || dayData[candidateName] || 0;
                        podcastsTotals.set(candidateName, current + value);
                    }
                });
            });

            // Calculate grand totals for percentages
            const grandTotalPodcasts = Array.from(podcastsTotals.values()).reduce((sum, val) => sum + val, 0);

            const csvData = candidateStats.map((candidate) => {
                const podcastsTotal = podcastsTotals.get(candidate.name) || 0;
                const allChannelsTotal = candidate.mentions;
                const radioTvTotal = allChannelsTotal - podcastsTotal;

                return {
                    'Candidato': candidate.name,
                    'Total - Menciones': allChannelsTotal,
                    'Total - Porcentaje': totalMentions > 0 ? ((allChannelsTotal / totalMentions) * 100).toFixed(2).replace('.', ',') : '0,00',
                    'Solo Podcasts - Menciones': podcastsTotal,
                    'Solo Podcasts - Porcentaje': grandTotalPodcasts > 0 ? ((podcastsTotal / grandTotalPodcasts) * 100).toFixed(2).replace('.', ',') : '0,00',
                };
            });

            const csvContent = convertToCSV(csvData);
            const filename = createDateRangeFilename('total_menciones_candidatos_completo', selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading total mentions CSV:', error);
        }
    };

    const downloadRankingCSV = async () => {
        if (!candidateStats.length) return;

        try {
            // Fetch detailed stats for podcasts only
            const podcastsOnlyData = await fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio: false,
                entityType: 'candidate',
                period: weeklyStatus,
            });

            // Calculate totals and stats for podcasts only
            const podcastsStats = new Map();
            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(candidateName => {
                    if (candidateName !== 'date' && candidateName !== 'fullDate') {
                        const current = podcastsStats.get(candidateName) || { mentions: 0, days: 0 };
                        // Handle the nested structure with .count
                        const value = dayData[candidateName]?.count || dayData[candidateName] || 0;
                        current.mentions += value;
                        if (value > 0) {
                            current.days += 1;
                        }
                        podcastsStats.set(candidateName, current);
                    }
                });
            });

            const csvData = candidateStats.map((candidate, index) => {
                const podcastsStat = podcastsStats.get(candidate.name) || { mentions: 0, days: 0 };
                const radioTvMentions = candidate.mentions - podcastsStat.mentions;
                const radioTvDays = candidate.daysActive - podcastsStat.days;

                return {
                    'Posición': index + 1,
                    'Candidato': candidate.name,
                    'Total - Menciones': candidate.mentions,
                    [`Total - ${t('charts.activeDays')}`]: candidate.daysActive,
                    'Total - Promedio por Día': candidate.avgMentionsPerDay.toString().replace('.', ','),
                    'Total - Promedio por Episodio': candidate.avgMentionsPerEpisode.toString().replace('.', ','),
                    'Total - Duración (horas)': candidate.duration.toString().replace('.', ','),
                    'Podcasts - Menciones': podcastsStat.mentions,
                    [`Podcasts - ${t('charts.activeDays')}`]: podcastsStat.days,
                    'Podcasts - Promedio por Día': podcastsStat.days > 0 ? (podcastsStat.mentions / podcastsStat.days).toFixed(2).replace('.', ',') : '0,00',
                    'Período': `${selectedRange.from} a ${selectedRange.to}`,
                };
            });

            const csvContent = convertToCSV(csvData);
            const filename = createDateRangeFilename('ranking_candidatos_completo', selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading ranking CSV:', error);
        }
    };

    // Process data for charts
    const { chartData, candidateStats, topicStats, totalMentions } = useMemo(() => {
        if (!dailyMentions.length) {
            return { chartData: [], candidateStats: [], topicStats: [], totalMentions: 0 };
        }

        // Get all unique candidates
        const candidatesSet = new Set<string>();
        dailyMentions.forEach(day => {
            Object.keys(day).forEach(key => {
                if (key !== 'date' && day[key]?.count > 0) {
                    // Clean up the candidate name
                    const cleanKey = key?.toString().trim();
                    if (cleanKey && cleanKey !== 'undefined' && cleanKey !== 'null') {
                        candidatesSet.add(cleanKey);
                    }
                }
            });
        });

        const candidates = Array.from(candidatesSet);

        // Get all unique topics
        const topicsSet = new Set<string>();
        dailyTopicMentions.forEach(day => {
            Object.keys(day).forEach(key => {
                if (key !== 'date' && key !== 'fullDate') {
                    const hasData = typeof day[key] === 'object'
                        ? (day[key]?.count > 0)
                        : (typeof day[key] === 'number' && day[key] > 0);
                    if (hasData) {
                        const cleanKey = key?.toString().trim();
                        if (cleanKey && cleanKey !== 'undefined' && cleanKey !== 'null') {
                            topicsSet.add(cleanKey);
                        }
                    }
                }
            });
        });
        const topics = Array.from(topicsSet);

        // Create a map of topic data by date for merging
        const topicDataByDate = new Map<string, any>();
        dailyTopicMentions.forEach(day => {
            topicDataByDate.set(day.date, day);
        });

        // Transform data for line chart (merging candidates and topics)
        const chartData = dailyMentions.map(day => {
            // Backend now handles daily/weekly aggregation
            // For weekly data, the date will be the week start date
            const dateObj = new Date(day.date + 'T12:00:00.000Z'); // Add time to avoid timezone issues

            let formattedDate;
            if (weeklyStatus === 'weekly') {
                // Format as "Sem 30 Jun" for weekly data
                formattedDate = `${t('charts.weekShort')} ${dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                })}`;
            } else {
                // Format as "30 Jun" for daily data
                formattedDate = dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                });
            }

            const transformedDay: any = {
                date: formattedDate,
                fullDate: day.date,
            };

            // Add candidate data
            candidates.forEach(candidate => {
                transformedDay[candidate] = day[candidate]?.count || 0;
            });

            // Add topic data (prefixed with "tema:" to differentiate)
            const topicDay = topicDataByDate.get(day.date);
            if (topicDay) {
                topics.forEach(topic => {
                    const count = typeof topicDay[topic] === 'object' && topicDay[topic]?.count !== undefined
                        ? topicDay[topic].count
                        : (typeof topicDay[topic] === 'number' ? topicDay[topic] : 0);
                    transformedDay[`tema:${topic}`] = count;
                });
            }

            return transformedDay;
        });

        // Calculate candidate statistics
        const candidateStats = candidates.map(candidate => {
            const totalMentions = dailyMentions.reduce((sum, day) => {
                return sum + (day[candidate]?.count || 0);
            }, 0);

            const totalDuration = dailyMentions.reduce((sum, day) => {
                return sum + (day[candidate]?.duration || 0);
            }, 0);

            const totalEpisodes = dailyMentions.reduce((sum, day) => {
                return sum + (day[candidate]?.episodeCount || 0);
            }, 0);

            const daysWithMentions = dailyMentions.filter(day => day[candidate]?.count > 0).length;

            return {
                name: candidate,
                mentions: totalMentions,
                duration: Math.round((totalDuration / 3600) * 10) / 10, // Convert to hours
                daysActive: daysWithMentions,
                avgMentionsPerDay: daysWithMentions > 0 ? Math.round((totalMentions / daysWithMentions) * 10) / 10 : 0,
                avgMentionsPerEpisode: totalEpisodes > 0 ? Math.round((totalMentions / totalEpisodes) * 10) / 10 : 0,
                image: CANDIDATE_IMAGES[candidate],
                color: CANDIDATE_COLORS[candidate] || '#8884d8',
            };
        }).sort((a, b) => b.mentions - a.mentions);

        // Calculate topic statistics
        const topicStats = topics.map(topic => {
            const totalMentions = dailyTopicMentions.reduce((sum, day) => {
                const count = typeof day[topic] === 'object' && day[topic]?.count !== undefined
                    ? day[topic].count
                    : (typeof day[topic] === 'number' ? day[topic] : 0);
                return sum + count;
            }, 0);

            return {
                name: topic,
                mentions: totalMentions,
                color: TOPIC_COLORS[topic] || '#888888',
            };
        }).sort((a, b) => b.mentions - a.mentions);

        const totalMentions = candidateStats.reduce((sum, candidate) => sum + candidate.mentions, 0);

        return { chartData, candidateStats, topicStats, totalMentions };
    }, [dailyMentions, dailyTopicMentions, weeklyStatus]);

    // Initialize selected candidates with top 4 when data changes
    React.useEffect(() => {
        if (candidateStats.length > 0 && selectedCandidatesForChart.length === 0) {
            const top4Candidates = candidateStats.slice(0, 4).map(c => c.name);
            setSelectedCandidatesForChart(top4Candidates);
        }
    }, [candidateStats, selectedCandidatesForChart.length]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        console.error('Error fetching daily mentions:', error);
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {t('empty.loadDaily', { message: error instanceof Error ? error.message : 'Unknown error' })}
            </Alert>
        );
    }

    if (!dailyMentions.length || !candidateStats.length) {
        return (
            <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                        {t('empty.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {t('empty.noCandidates')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('empty.because')}
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mt: 2 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.noAnalyzed')}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.stillProcessing')}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.periodNoCandidates')}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Stack spacing={4}>
            {/* Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                {candidateStats.slice(0, 4).map((candidate) => (
                    <Card
                        key={candidate.name}
                        sx={{
                            cursor: 'pointer',
                            border: `2px solid ${theme.palette.divider}`,
                            backgroundColor: 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                border: `2px solid ${theme.palette.divider}`,
                            }
                        }}
                        onClick={() => handleCandidateClick(candidate.name)}
                    >
                        <CardContent sx={{ pb: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    src={candidate.image}
                                    alt={candidate.name}
                                    sx={{ width: 48, height: 48 }}
                                >
                                    {candidate.name.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                        {candidate.name}
                                    </Typography>
                                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '1.5rem' }}>
                                        {candidate.mentions}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mentions.word')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Line Chart - Daily Mentions Over Time */}
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h3">
                        {weeklyStatus === 'daily' ? (selectedTopicsForChart.length > 0 ? t('charts.mentionsByCandidateTopicsDaily') : t('charts.mentionsByCandidateDaily')) : (selectedTopicsForChart.length > 0 ? t('charts.mentionsByCandidateTopicsWeekly') : t('charts.mentionsByCandidateWeekly'))}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel id="weekly-status-label">{t('period.label')}</InputLabel>
                            <Select
                                labelId="weekly-status-label"
                                value={weeklyStatus}
                                onChange={(e) => setWeeklyStatus(e.target.value as 'daily' | 'weekly')}
                                input={<OutlinedInput label={t('period.label')} />}
                            >
                                <MenuItem value="daily">{t('cadence.daily')}</MenuItem>
                                <MenuItem value="weekly">{t('cadence.weekly')}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 300 }}>
                            <InputLabel id="candidate-select-label">{t('fields.selectCandidates')}</InputLabel>
                            <Select
                                labelId="candidate-select-label"
                                multiple
                                value={selectedCandidatesForChart}
                                onChange={handleCandidateSelectionChange}
                                input={<OutlinedInput label={t('fields.selectCandidates')} />}
                                renderValue={(selected) => `${selected.length} candidatos seleccionados`}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400,
                                        },
                                    },
                                }}
                            >
                                {candidateStats.map((candidate) => (
                                    <MenuItem
                                        key={candidate.name}
                                        value={candidate.name}
                                        sx={{
                                            pl: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedCandidatesForChart.indexOf(candidate.name) > -1}
                                            sx={{
                                                color: candidate.color,
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '50%',
                                                },
                                                '&.Mui-checked .MuiSvgIcon-root': {
                                                    backgroundColor: candidate.color,
                                                    borderRadius: '50%',
                                                },
                                                mr: 1,
                                                p: 0.5,
                                            }}
                                            icon={<Box sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                border: `2px solid ${candidate.color}`,
                                                backgroundColor: 'transparent'
                                            }} />}
                                            checkedIcon={<Box sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                backgroundColor: candidate.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>✓</Box>}
                                        />
                                        <Avatar
                                            src={candidate.image}
                                            alt={candidate.name}
                                            sx={{ width: 24, height: 24, mr: 1 }}
                                        >
                                            {candidate.name.charAt(0)}
                                        </Avatar>
                                        <ListItemText
                                            primary={candidate.name}
                                            secondary={`${t('mentions.count', { count: candidate.mentions })}`}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 250 }}>
                            <InputLabel id="topic-select-label">{t('fields.addTopics')}</InputLabel>
                            <Select
                                labelId="topic-select-label"
                                multiple
                                value={selectedTopicsForChart}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedTopicsForChart(typeof value === 'string' ? value.split(',') : value);
                                }}
                                input={<OutlinedInput label={t('fields.addTopics')} />}
                                renderValue={(selected) => selected.length > 0 ? t('fields.topicsCount', { count: selected.length }) : t('fields.none')}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400,
                                        },
                                    },
                                }}
                            >
                                {topicStats.map((topic) => (
                                    <MenuItem
                                        key={topic.name}
                                        value={topic.name}
                                        sx={{
                                            pl: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedTopicsForChart.indexOf(topic.name) > -1}
                                            sx={{
                                                color: topic.color,
                                                '&.Mui-checked': {
                                                    color: topic.color,
                                                },
                                                mr: 1,
                                                p: 0.5,
                                            }}
                                        />
                                        <ListItemText
                                            primary={topicLabel(topic.name)}
                                            secondary={`${t('mentions.count', { count: topic.mentions })}`}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <CsvDownloadButton
                            onClick={downloadDailyMentionsCSV}
                            disabled={!chartData.length || selectedCandidatesForChart.length === 0}
                        />
                    </Stack>
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: 1, borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                fontSize: '1.1rem',
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {t('charts.evolution')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {weeklyStatus === 'weekly' ? t('charts.trendCandidateWeekly') : t('charts.trendCandidateDaily')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ width: '100%', height: 400, pt: 1 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData} margin={{ top: 30, right: 50, left: 10, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        interval="preserveStartEnd"
                                        type="category"
                                        scale="point"
                                        tickFormatter={(value) => value}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                    />
                                    <Tooltip
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload[0]) {
                                                // Fix timezone issue by adding time component
                                                const dateStr = payload[0].payload.fullDate;
                                                const dateObj = new Date(dateStr + 'T12:00:00.000Z');
                                                return dateObj.toLocaleDateString('es-CL', {
                                                    timeZone: 'America/Santiago',
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                });
                                            }
                                            return label;
                                        }}
                                        formatter={(value, name) => [
                                            t('charts.tooltipMentions', { count: Number(value ?? 0) }),
                                            name as string
                                        ]}
                                        itemSorter={(item) => {
                                            // Sort by value (mention count) in descending order
                                            return -(item.value as number);
                                        }}
                                    />
                                    <Legend />
                                    {/* Election milestone reference lines */}
                                    {ELECTION_MILESTONES.map((milestone) => {
                                        const xValue = findMilestoneLabel(chartData, milestone.date);
                                        if (!xValue) return null;
                                        return (
                                            <ReferenceLine
                                                key={milestone.date}
                                                x={xValue}
                                                stroke={milestone.color}
                                                strokeWidth={2}
                                                strokeDasharray="4 4"
                                                label={<ReferenceLineLabel value={t(`milestones.${milestone.id}.short`)} fill={milestone.color} fullLabel={t(`milestones.${milestone.id}.full`)} />}
                                            />
                                        );
                                    })}
                                    {candidateStats
                                        .filter(candidate => selectedCandidatesForChart.includes(candidate.name))
                                        .map((candidate) => (
                                            <Line
                                                key={candidate.name}
                                                type="monotone"
                                                dataKey={candidate.name}
                                                stroke={candidate.color}
                                                strokeWidth={2}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                        ))}
                                    {/* Topic lines - dashed to differentiate from candidates */}
                                    {topicStats
                                        .filter(topic => selectedTopicsForChart.includes(topic.name))
                                        .map((topic) => (
                                            <Line
                                                key={`tema:${topic.name}`}
                                                type="monotone"
                                                dataKey={`tema:${topic.name}`}
                                                name={topicLabel(topic.name)}
                                                stroke={topic.color}
                                                strokeWidth={2}
                                                strokeDasharray="5 5"
                                                dot={{ r: 3 }}
                                                activeDot={{ r: 5 }}
                                            />
                                        ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                </Paper>
            </Stack>

            {/* Bar Chart - Total Mentions */}
            <Stack spacing={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h3">
                        {t('charts.totalByCandidate')}
                    </Typography>
                    <CsvDownloadButton
                        onClick={downloadTotalMentionsCSV}
                        disabled={!candidateStats.length}
                    />
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: 1, borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                fontSize: '1.1rem',
                            }}
                        >
                            {t('charts.rankingCandidates')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {t('charts.totalInPeriod')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={candidateStats}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        interval={0}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                            border: 'none',
                                            borderRadius: 12,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            padding: '12px 16px',
                                        }}
                                        formatter={(value) => [t('charts.tooltipMentions', { count: Number(value ?? 0) }), t('charts.total')]}
                                        labelFormatter={(label) => `${label}`}
                                    />
                                    <Bar
                                        dataKey="mentions"
                                        radius={[6, 6, 0, 0]}
                                    >
                                        {candidateStats.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                </Paper>
            </Stack>

            {/* Detailed Statistics */}
            <Stack spacing={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2">
                        {t('charts.rankingCandidates')}
                    </Typography>
                    <CsvDownloadButton
                        onClick={downloadRankingCSV}
                        disabled={!candidateStats.length}
                    />
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        {/* Header Row */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                            mb: 2,
                            fontWeight: 600,
                            border: '1px solid', borderColor: 'divider',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ minWidth: 24, textAlign: 'center', mr: 2, fontWeight: 600 }}>
                                    #
                                </Typography>
                                <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                                    {t('fields.candidate')}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {t('charts.activeDays')}
                                    </Typography>
                                </Box>
                                <MuiTooltip
                                    title={t('charts.avgDayTipCandidate')}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ textAlign: 'center', cursor: 'help', minWidth: 80 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {t('charts.avgPerDay')}
                                        </Typography>
                                    </Box>
                                </MuiTooltip>
                                <MuiTooltip
                                    title={t('charts.avgEpisodeTipCandidate')}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ textAlign: 'center', cursor: 'help', minWidth: 100 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {t('charts.avgPerEpisode')}
                                        </Typography>
                                    </Box>
                                </MuiTooltip>
                                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {t('charts.shareOfTotal')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Data Rows */}
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            {candidateStats.map((candidate, index) => (
                                <Box
                                    key={candidate.name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        bgcolor: index % 2 === 0 ? 'grey.50' : 'transparent',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'primary.50',
                                            transform: 'translateX(4px)',
                                            transition: 'all 0.2s ease-in-out'
                                        }
                                    }}
                                    onClick={() => handleCandidateClick(candidate.name)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ minWidth: 24, textAlign: 'center', mr: 2 }}
                                        >
                                            #{index + 1}
                                        </Typography>
                                        <Avatar
                                            src={candidate.image}
                                            alt={candidate.name}
                                            sx={{ width: 40, height: 40, mr: 2 }}
                                        >
                                            {candidate.name.charAt(0)}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {candidate.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('mentions.count', { count: candidate.mentions })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {candidate.daysActive}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {candidate.avgMentionsPerDay}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {candidate.avgMentionsPerEpisode}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {totalMentions > 0 ? Math.round((candidate.mentions / totalMentions) * 100) : 0}%
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </Stack>

            {/* Sidebar for candidate mentions */}
            <Drawer
                anchor="right"
                open={sidebarOpen}
                onClose={handleCloseSidebar}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 800,
                        maxWidth: '90vw',
                    },
                }}
            >
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                                src={selectedCandidate ? CANDIDATE_IMAGES[selectedCandidate] : undefined}
                                alt={selectedCandidate || ''}
                                sx={{ width: 40, height: 40 }}
                            >
                                {selectedCandidate?.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h6">{selectedCandidate}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('mentions.inPeriod')}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={handleCloseSidebar}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Content */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {isLoadingMentions ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                <CircularProgress />
                            </Box>
                        ) : candidateMentionsData?.mentions.length ? (
                            <Stack spacing={2}>
                                {candidateMentionsData.mentions.map((mention) => (
                                    <Card key={mention._id} variant="outlined">
                                        <CardContent sx={{ pb: 2 }}>
                                            <Stack spacing={2}>
                                                {/* Episode info */}
                                                <Box>
                                                    <Typography variant="subtitle2" color="primary.main">
                                                        {mention.podcastName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {mention.episodeTitle}
                                                    </Typography>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(mention.publishedAt).toLocaleDateString('es-CL', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                            })}
                                                        </Typography>
                                                        {mention.episodeUrl && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const youtubeUrl = createYouTubeUrlWithTimestamp(
                                                                        mention.episodeUrl,
                                                                        mention.segmentStart
                                                                    );
                                                                    window.open(youtubeUrl, '_blank');
                                                                }}
                                                                sx={{ p: 0.5 }}
                                                            >
                                                                <LaunchIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </Box>

                                                {/* Mention content */}
                                                <Box>
                                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                                        {mention.mentionText}
                                                    </Typography>
                                                    {mention.context && mention.context !== mention.mentionText && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            Contexto: {mention.context}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Metadata */}
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    {mention.primaryTopicName ? (
                                                        <Chip
                                                            label={mention.primaryTopicName}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    ) : mention.subtopics.length > 0 && (
                                                        <Chip
                                                            label={mention.subtopics[0]}
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    )}
                                                    {mention.subtopics.length > 1 && (
                                                        <Chip
                                                            label={mention.subtopics.slice(0, 3).join(', ')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {t('mentions.noneCandidate')}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Pagination */}
                    {candidateMentionsData?.pagination && candidateMentionsData.pagination.totalPages > 1 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={candidateMentionsData.pagination.totalPages}
                                page={currentPage}
                                onChange={(_, page) => setCurrentPage(page)}
                                color="primary"
                            />
                        </Box>
                    )}
                </Box>
            </Drawer>
        </Stack>
    );
}
